import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { overrideAPI } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { isKRRole } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  FileText, Search, Filter, ChevronLeft, ChevronRight, Loader2,
  CheckCircle2, Clock, DollarSign, Eye, ThumbsUp, Undo2, Plus, Download, CreditCard, AlertTriangle, Trash2,
} from 'lucide-react';

const STATUS_COLORS = {
  Draft: 'bg-slate-100 text-slate-700',
  Generated: 'bg-sky-100 text-sky-700',
  Paid: 'bg-emerald-100 text-emerald-700',
  Voided: 'bg-red-100 text-red-700',
};

const fmtMoney = (v) => {
  const n = Number(v);
  if (isNaN(n)) return '$0.00';
  return n < 0 ? `-$${Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2 })}` : `$${n.toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
};

export default function StatementsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canManage = isKRRole(user);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = 25;
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filters, setFilters] = useState({ status: 'all', payee_type: 'all', statement_month: 'all' });
  const [showFilters, setShowFilters] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [generateOpen, setGenerateOpen] = useState(false);
  const [genMonth, setGenMonth] = useState('');
  const [genPayeeIds, setGenPayeeIds] = useState('');
  const [generating, setGenerating] = useState(false);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [voidDialog, setVoidDialog] = useState(null);
  const [voidReason, setVoidReason] = useState('');
  const [voiding, setVoiding] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page, page_size: pageSize,
        status: filters.status !== 'all' ? filters.status : undefined,
        payee_type: filters.payee_type !== 'all' ? filters.payee_type : undefined,
        statement_month: filters.statement_month !== 'all' ? filters.statement_month : undefined,
      };
      const res = await overrideAPI.statements.list(params);
      // Filter by search client-side (payee_name search)
      let data = res.data.items || [];
      if (debouncedSearch) {
        data = data.filter(s => s.payee_name?.toLowerCase().includes(debouncedSearch.toLowerCase()));
      }
      setItems(data);
      setTotal(res.data.total);
    } catch {
      toast.error('Failed to load statements');
    }
    setLoading(false);
  }, [page, pageSize, debouncedSearch, filters]);

  useEffect(() => { loadData(); }, [loadData]);

  // Load available months for generate dialog
  useEffect(() => {
    overrideAPI.months().then(res => {
      setAvailableMonths(res.data?.months || res.data || []);
    }).catch(() => {});
  }, []);

  const openDetail = async (id) => {
    setDetailId(id);
    setDetailLoading(true);
    try {
      const res = await overrideAPI.statements.get(id);
      setDetail(res.data);
    } catch {
      toast.error('Failed to load statement');
    }
    setDetailLoading(false);
  };

  const handleGenerate = async () => {
    if (!genMonth) { toast.error('Select a month'); return; }
    setGenerating(true);
    try {
      const payeeIds = genPayeeIds ? genPayeeIds.split(',').map(s => s.trim()).filter(Boolean) : null;
      const res = await overrideAPI.statements.generate(genMonth, payeeIds);
      toast.success(`Generated ${res.data.statements_created} statement(s) for ${genMonth}`);
      setGenerateOpen(false);
      setGenMonth('');
      setGenPayeeIds('');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate statements');
    }
    setGenerating(false);
  };

  const handleVoid = async () => {
    if (!voidReason.trim()) { toast.error('Please provide a reason'); return; }
    setVoiding(true);
    try {
      await overrideAPI.statements.void(voidDialog.statement_id, voidReason);
      toast.success('Statement voided');
      setVoidDialog(null);
      setVoidReason('');
      loadData();
      if (detailId === voidDialog.statement_id) setDetailId(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to void statement');
    }
    setVoiding(false);
  };

  const handleDownloadPdf = async (statementId, payeeName, month) => {
    try {
      const res = await overrideAPI.statements.downloadPdf(statementId);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `statement_${payeeName.replace(/\s+/g, '_')}_${month}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch {
      toast.error('Failed to download PDF');
    }
  };

  const totalPages = Math.ceil(total / pageSize);
  const activeFilterCount = Object.values(filters).filter(v => v !== 'all').length;

  return (
    <div className="space-y-6" data-testid="statements-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-slate-500 uppercase tracking-wider">Accounting</p>
          <h1 className="text-2xl font-heading font-bold text-slate-900">Payout Statements</h1>
          <p className="text-sm text-slate-500 mt-1">Statements generated from approved allocations in Override Reconciliation</p>
        </div>
        {canManage && (
          <Button size="sm" className="gap-1.5" onClick={() => setGenerateOpen(true)} data-testid="generate-statements-btn">
            <Plus size={14} /> Generate Statements
          </Button>
        )}
      </div>

      {/* Search & Filters */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-xs">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search payee name..." className="pl-9 h-9 text-sm" data-testid="stmt-search" />
        </div>
        <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)} className="gap-1.5" data-testid="toggle-stmt-filters">
          <Filter size={14} /> Filters
          {activeFilterCount > 0 && <Badge variant="secondary" className="ml-1 h-4 px-1 text-[10px]">{activeFilterCount}</Badge>}
        </Button>
      </div>

      {showFilters && (
        <div className="flex flex-wrap gap-2 p-3 bg-slate-50 rounded-lg border border-slate-200" data-testid="stmt-filter-panel">
          <Select value={filters.status} onValueChange={v => { setFilters(f => ({ ...f, status: v })); setPage(1); }}>
            <SelectTrigger className="w-32 h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="Generated">Generated</SelectItem>
              <SelectItem value="Paid">Paid</SelectItem>
              <SelectItem value="Voided">Voided</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filters.payee_type} onValueChange={v => { setFilters(f => ({ ...f, payee_type: v })); setPage(1); }}>
            <SelectTrigger className="w-32 h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="Agent">Agent</SelectItem>
              <SelectItem value="Agency">Agency</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filters.statement_month} onValueChange={v => { setFilters(f => ({ ...f, statement_month: v })); setPage(1); }}>
            <SelectTrigger className="w-32 h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Months</SelectItem>
              {availableMonths.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="animate-spin text-slate-400" size={28} /></div>
          ) : items.length === 0 ? (
            <div className="py-16 text-center" data-testid="statements-empty">
              <FileText size={36} className="mx-auto mb-3 text-slate-300" />
              <p className="text-sm font-medium text-slate-500">No statements generated yet</p>
              <p className="text-xs text-slate-400 mt-1">Generate statements from approved allocations to get started</p>
              {canManage && (
                <Button size="sm" variant="outline" className="mt-4 gap-1.5" onClick={() => setGenerateOpen(true)}>
                  <Plus size={14} /> Generate Statements
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="statements-table">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs text-slate-500 uppercase">
                    <th className="px-4 py-3">Payee</th>
                    <th className="px-4 py-3">Month</th>
                    <th className="px-4 py-3">Lines</th>
                    <th className="px-4 py-3 text-right">Total</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Payment</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(s => (
                    <tr key={s.statement_id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3">
                        <p className="font-medium text-slate-800">{s.payee_name}</p>
                        <p className="text-xs text-slate-400">{s.payee_type}</p>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs">{s.statement_month}</td>
                      <td className="px-4 py-3 text-center">{s.line_count}</td>
                      <td className="px-4 py-3 text-right font-mono font-bold">{fmtMoney(s.total_amount)}</td>
                      <td className="px-4 py-3">
                        <Badge className={`text-xs ${STATUS_COLORS[s.status] || ''}`}>{s.status}</Badge>
                      </td>
                      <td className="px-4 py-3">
                        <PaymentBadge status={s.payment_status} />
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center gap-1 justify-end">
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openDetail(s.statement_id)} data-testid={`stmt-view-${s.statement_id}`}>
                            <Eye size={14} />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-500" onClick={() => handleDownloadPdf(s.statement_id, s.payee_name, s.statement_month)} data-testid={`stmt-pdf-${s.statement_id}`}>
                            <Download size={14} />
                          </Button>
                          {canManage && s.status === 'Generated' && (
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-600" onClick={() => { setVoidDialog(s); setVoidReason(''); }} data-testid={`stmt-void-${s.statement_id}`} title="Void Statement">
                              <AlertTriangle size={14} />
                            </Button>
                          )}
                          {canManage && (
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-red-600" 
                              title="Delete Statement"
                              onClick={async () => {
                                if (window.confirm(`Delete statement for "${s.payee_name}" (${s.statement_month})?`)) {
                                  try {
                                    await overrideAPI.statements.delete(s.statement_id);
                                    toast.success('Statement deleted');
                                    loadData();
                                  } catch (err) {
                                    toast.error(err.response?.data?.detail || 'Failed to delete statement');
                                  }
                                }
                              }}
                              data-testid={`stmt-delete-${s.statement_id}`}
                            >
                              <Trash2 size={14} />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm" data-testid="stmt-pagination">
          <span className="text-slate-500">Page {page} of {totalPages} ({total} total)</span>
          <div className="flex gap-1">
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}>
              <ChevronLeft size={14} />
            </Button>
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>
              <ChevronRight size={14} />
            </Button>
          </div>
        </div>
      )}

      {/* Generate Dialog */}
      <Dialog open={generateOpen} onOpenChange={setGenerateOpen}>
        <DialogContent className="max-w-sm" data-testid="generate-stmt-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Generate Statements</DialogTitle>
            <DialogDescription>Create statements from all approved allocations for the selected month, grouped by payee</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs">Statement Month *</Label>
              <Select value={genMonth} onValueChange={setGenMonth}>
                <SelectTrigger data-testid="gen-month-select"><SelectValue placeholder="Select month" /></SelectTrigger>
                <SelectContent>
                  {availableMonths.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Payee IDs (optional, comma-separated)</Label>
              <Input value={genPayeeIds} onChange={e => setGenPayeeIds(e.target.value)} placeholder="Leave empty for all payees" className="h-9 text-sm" data-testid="gen-payee-ids" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGenerateOpen(false)}>Cancel</Button>
            <Button onClick={handleGenerate} disabled={generating} data-testid="confirm-generate-btn">
              {generating ? <Loader2 size={14} className="animate-spin mr-1" /> : null} Generate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Void Dialog */}
      <Dialog open={!!voidDialog} onOpenChange={() => setVoidDialog(null)}>
        <DialogContent className="max-w-sm" data-testid="void-stmt-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-red-600">Void Statement</DialogTitle>
            <DialogDescription>This action cannot be undone. The allocations will be unlocked.</DialogDescription>
          </DialogHeader>
          {voidDialog && (
            <div className="space-y-3">
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm">
                <p><strong>{voidDialog.payee_name}</strong> — {voidDialog.statement_month}</p>
                <p className="font-mono">{fmtMoney(voidDialog.total_amount)}</p>
              </div>
              <div>
                <Label className="text-xs">Reason *</Label>
                <Input value={voidReason} onChange={e => setVoidReason(e.target.value)} placeholder="Why is this being voided?" className="h-9 text-sm" data-testid="void-reason" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setVoidDialog(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleVoid} disabled={voiding} data-testid="confirm-void-btn">
              {voiding ? <Loader2 size={14} className="animate-spin mr-1" /> : null} Void Statement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Statement Detail Dialog */}
      <Dialog open={!!detailId} onOpenChange={() => { setDetailId(null); setDetail(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="stmt-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Statement Detail</DialogTitle>
            <DialogDescription>View statement information and allocation line items</DialogDescription>
          </DialogHeader>
          {detailLoading ? (
            <div className="flex items-center justify-center py-10"><Loader2 className="animate-spin text-slate-400" size={24} /></div>
          ) : detail ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-slate-500">Payee:</span> <strong>{detail.payee_name}</strong></div>
                <div><span className="text-slate-500">Type:</span> {detail.payee_type}</div>
                <div><span className="text-slate-500">Month:</span> <span className="font-mono">{detail.statement_month}</span></div>
                <div><span className="text-slate-500">Status:</span> <Badge className={`text-xs ${STATUS_COLORS[detail.status] || ''}`}>{detail.status}</Badge></div>
                <div><span className="text-slate-500">Lines:</span> {detail.line_count}</div>
                <div><span className="text-slate-500">Total:</span> <span className="font-mono font-bold">{fmtMoney(detail.total_amount)}</span></div>
                {detail.payment_date && <div><span className="text-slate-500">Payment Date:</span> <span className="font-mono">{detail.payment_date}</span></div>}
                {detail.payment_method && <div><span className="text-slate-500">Payment Method:</span> {detail.payment_method}</div>}
              </div>

              {/* Line Items (Allocations) */}
              <div>
                <h4 className="text-sm font-medium text-slate-700 mb-2">Allocation Line Items ({detail.line_items?.length || 0})</h4>
                <div className="max-h-48 overflow-y-auto border rounded-lg">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr className="text-left text-slate-500">
                        <th className="px-3 py-2">Writing Agent</th>
                        <th className="px-3 py-2">Client</th>
                        <th className="px-3 py-2">Type</th>
                        <th className="px-3 py-2 text-right">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(detail.line_items || []).map((item, i) => (
                        <tr key={i} className="border-t border-slate-100">
                          <td className="px-3 py-1.5">{item.writing_agent_name || '-'}</td>
                          <td className="px-3 py-1.5">{item.client_name || '-'}</td>
                          <td className="px-3 py-1.5">{item.payment_type || '-'}</td>
                          <td className="px-3 py-1.5 text-right font-mono">{fmtMoney(item.allocation_amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Actions */}
              {canManage && (
                <div className="flex gap-2 pt-2 border-t">
                  {detail.status === 'Generated' && (
                    <>
                      <Button size="sm" className="gap-1.5 bg-emerald-600 hover:bg-emerald-700" onClick={() => navigate('/accounting/payments')} data-testid="detail-record-payment-btn">
                        <CreditCard size={14} /> Record Payment
                      </Button>
                      <Button variant="outline" size="sm" className="gap-1.5 text-red-600" onClick={() => { setVoidDialog(detail); setVoidReason(''); }} data-testid="detail-void-btn">
                        <AlertTriangle size={14} /> Void
                      </Button>
                    </>
                  )}
                  <Button variant="outline" size="sm" className="gap-1.5 ml-auto" onClick={() => handleDownloadPdf(detail.statement_id, detail.payee_name, detail.statement_month)} data-testid="detail-pdf-btn">
                    <Download size={14} /> Download PDF
                  </Button>
                </div>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PaymentBadge({ status }) {
  if (!status) return <span className="text-xs text-slate-400">—</span>;
  const map = {
    Paid: 'bg-emerald-100 text-emerald-700',
    Scheduled: 'bg-amber-100 text-amber-700',
    Sent: 'bg-sky-100 text-sky-700',
  };
  return <Badge className={`text-xs ${map[status] || 'bg-slate-100 text-slate-600'}`}>{status}</Badge>;
}
